/**
 * File validation utilities for preventing XSS attacks and malicious filenames
 */

/**
 * Validates a filename to prevent XSS and other security vulnerabilities
 * Only allows alphanumeric characters, underscores, hyphens, and dots
 * 
 * @param {string} filename - The filename to validate
 * @returns {Object} - { isValid: boolean, errorMessage: string | null }
 */
export function validateFilename(filename) {
  if (!filename || typeof filename !== 'string') {
    return {
      isValid: false,
      errorMessage: 'Invalid filename provided'
    };
  }

  // Check filename length
  if (filename.length > 255) {
    return {
      isValid: false,
      errorMessage: 'Filename is too long (maximum 255 characters)'
    };
  }

  // Check if filename contains only allowed characters: a-z, A-Z, 0-9, _, -, .
  const validCharPattern = /^[a-zA-Z0-9_.-]+$/;

  if (!validCharPattern.test(filename)) {
    return {
      isValid: false,
      errorMessage: "File Name contains invalid characters. Only '_' or '-' are allowed"
    };
  }

  // Ensure filename has at least one alphanumeric character
  if (!/[a-zA-Z0-9]/.test(filename)) {
    return {
      isValid: false,
      errorMessage: "File Name must contain at least one alphanumeric character"
    };
  }

  return {
    isValid: true,
    errorMessage: null
  };
}

/**
 * Validates if a filename contains potentially dangerous characters
 * @param {string} filename - The filename to validate
 * @returns {Object} - { isSecure: boolean, threats: string[] }
 */
export function validateFilenameSecurity(filename) {
  if (!filename || typeof filename !== 'string') {
    return { isSecure: false, threats: ['Invalid filename'] };
  }

  const threats = [];

  // Check for HTML/XML tags
  if (/<[^>]*>/g.test(filename)) {
    threats.push('HTML/XML tags detected');
  }

  // Check for JavaScript execution attempts
  if (/javascript:|vbscript:|onload=|onerror=|onclick=/gi.test(filename)) {
    threats.push('Script execution attempts detected');
  }

  // Check for path traversal attempts
  if (/\.\.\/|\.\.\\|\.\.%2f|\.\.%5c/gi.test(filename)) {
    threats.push('Path traversal attempts detected');
  }

  // Check for null bytes or control characters
  if (/[\u0000-\u001f\u007f-\u009f]/g.test(filename)) {
    threats.push('Control characters or null bytes detected');
  }

  // Check for SQL injection attempts
  if (/['";]|--|\*|union\s+select/gi.test(filename)) {
    threats.push('Potential SQL injection attempts detected');
  }

  return {
    isSecure: threats.length === 0,
    threats
  };
}

/**
 * Main function to validate a filename for security and format
 * @param {string} originalFilename - The original filename
 * @returns {Object} - Complete validation result
 */
export function validateSecureFilename(originalFilename) {
  // Always check for security threats first
  const securityCheck = validateFilenameSecurity(originalFilename);

  // Then do basic filename validation
  const basicValidation = validateFilename(originalFilename);

  // Determine error message based on validation results
  let errorMessage = null;
  if (!basicValidation.isValid) {
    errorMessage = basicValidation.errorMessage;
  } else if (!securityCheck.isSecure) {
    errorMessage = "File Name contains invalid characters. Only '_' or '-' are allowed";
  }

  return {
    isValid: basicValidation.isValid && securityCheck.isSecure,
    errorMessage,
    securityThreats: securityCheck.threats,
    hadSecurityThreats: securityCheck.isSecure === false,
    originalFilename
  };
}

/**
 * Sanitizes text to prevent XSS attacks by escaping HTML special characters
 * This prevents script injection when user input is rendered in the UI
 * 
 * @param {string} text - The text to sanitize
 * @returns {string} - The sanitized text safe for rendering in HTML
 */
export function sanitizeHtml(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const htmlEscapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '=': '&#x3D;'
  };

  return text.replace(/[&<>"'/=]/g, (char) => htmlEscapeMap[char]);
}

/**
 * Sanitizes an array of error messages to prevent XSS attacks
 * 
 * @param {string[]} errors - Array of error messages
 * @returns {string[]} - Array of sanitized error messages
 */
export function sanitizeErrors(errors) {
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.map(error => sanitizeHtml(error));
}
